import { React, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import '../../styles/TableForFilesManager.css';
import Table from '../Table/Table';
import CustomButtonUpload from '../CustomElements/CustomButtonUpload';
import { useRole } from '../../customHook/useRole';
import { downloadFileFromServer, initFilesFromServer } from '../../redux/actions/files.action';
import { newFileFromServer } from '../../redux/actions/files.action';
import { defaultCellRender } from '../../utils/formatValueForHTML';
import { convertDateToString } from '../../utils/convertToDate';
import Breadcrumbs from './Breadcrumbs';
import CustomButton from '../CustomElements/CustomButton';

import AddFolderModal from './AddFolderModal';
import MoveFileModal from './MoveFileModal';
import Modal from '../Modal/Modal';
import { findFolderById } from '../../utils/findFolderById';
import useTranslations from '../../customHook/useTranslations';
import translations from '../../content/PagesContent/FilesPages/filePageTranslations.json';

function TableForFilesManager(props) {
  // получение словаря для статических данных в зависимости от выбранного языка
  const t = useTranslations(translations);
  // Инициализация dispatch для работы с Redux
  const dispatch = useDispatch();
  // Данные файлов и папок
  const files = useSelector((state) => state.files);

  // Проверка роли пользователя
  const isMaster = useRole('master');

  // Состояния для хранения данных о текущем состоянии файлового менеджера
  const [currentFolder, setCurrentFolder] = useState(null); // Текущая открытая папка
  const [breadcrumbs, setBreadcrumbs] = useState([]); // Хлебные крошки для навигации
  const [moveFile, setMoveFile] = useState(null); // Файл, который нужно переместить
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false); // Состояние модального окна перемещения

  //данные файлов и папок для отображения в таблице
  const tableData = useMemo(() => {
    return currentFolder ? [...(currentFolder.children || [])] : files;
  }, [currentFolder, files]);

  // обновляем состояние текущей папки при изменении данных файлов
  useEffect(() => {
    if (currentFolder && files.length > 0) {
      for (const rootFolder of files) {
        const updated = findFolderById(rootFolder, currentFolder.id);
        if (updated) {
          setCurrentFolder(updated);
          break;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  // Состояния для модальных окон
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  /**
   * Закрытие всех модальных окон
   */
  const closeModal = () => {
    setIsOpen(false);
    setIsFolderModalOpen(false);
    setIsMoveModalOpen(false);
  };

  /**
   * Открытие папки и обновление навигации (хлебных крошек)
   * @param {Object} folder - Объект открываемой папки
   */
  const openFolder = (folder) => {
    setCurrentFolder(folder);
    setBreadcrumbs((prev) => [...prev, folder]);
  };

  /**
   * Открытие модального окна перемещения файла
   * @param {Object} file - Файл, который нужно переместить
   * @param {Event} event - Событие клика
   */
  const openMoveModal = (file, event) => {
    event.stopPropagation();
    setMoveFile(file);
    setIsMoveModalOpen(true);
    setIsOpen(true);
  };

  /**
   * Навигация назад по папкам
   * @param {number} index - Индекс папки в хлебных крошках
   */
  const goBackToFolder = (index) => {
    if (index === -1) {
      setCurrentFolder(null);
      setBreadcrumbs([]);
    } else {
      setCurrentFolder(breadcrumbs[index]);
      setBreadcrumbs(breadcrumbs.slice(0, index + 1));
    }
  };

  /**
   * Обработчик скачивания файла
   * @param {Object} file - Файл, который нужно скачать
   * @param {Event} event - Событие клика
   */
  const handleButtonClickAction = (file, event) => {
    event.stopPropagation();
    const payload = { fileId: file?.id, fileName: file?.name };
    dispatch(downloadFileFromServer(payload));
  };

  /**
   * Определение колонок для таблицы
   */
  const columns = useMemo(
    () => [
      {
        Header: t?.fileName || 'Название',
        accessor: 'name',
        Cell: ({ row }) => (
          <div
            className="file-folder-container"
            onClick={() => row.original.type === 'folder' && openFolder(row.original)}
          >
            <img
              src={`/image/${row.original.type === 'folder' ? 'folder.png' : 'sheet.png'}`}
              alt={row.original.type === 'folder' ? 'Folder' : 'File'}
              className="file-folder-icon img-size"
            />
            <span className="file-folder-name">{row.original.name}</span>
          </div>
        ),
      },

      {
        Header: t?.fileType || 'Тип',
        accessor: 'type',
        Cell: ({ value }) => defaultCellRender(value === 'folder' ? 'папка' : 'файл'),
      },
      {
        Header: t?.fileDateCreated || 'Дата создания',
        accessor: 'date',
        Cell: ({ value }) => defaultCellRender(convertDateToString(value)),
      },
      {
        Header: t?.fileDownload || 'Скачать',
        accessor: 'download',
        disableSorting: true, // Отключение сортировки
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        Cell: ({ row }) =>
          row.original.type === 'file' && (
            <button
              className="custom-button-edit"
              onClick={(event) => {
                handleButtonClickAction(row.original, event);
              }}
            >
              <img className="img-size filter" src="/image/file.png" alt="Скачать" />
            </button>
          ),
      },
      {
        Header: t?.fileMove || 'Переместить',
        accessor: 'move',
        Cell: ({ row }) =>
          row.original.type === 'file' && (
            <button className="move-button" onClick={(event) => openMoveModal(row.original, event)}>
              <img className="img-size" src="/image/exchange.png" alt="Переместить" />
            </button>
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [files, t]
  );

  // Инициализация данных файлов и папок при загрузке компонента
  useEffect(() => {
    dispatch(initFilesFromServer());
  }, [dispatch]);

  return (
    <>
      <div className="table-for-files-container">
        {/* Отображение таблицы */}
        <Table
          data={tableData}
          columns={columns}
          showGlobalSearch={true}
          breadcrumbs={<Breadcrumbs breadcrumbs={breadcrumbs} onNavigate={goBackToFolder} t={t} />}
          autoResetGlobalFilter={false}
        />

        {/* Блок с кнопками */}
        <div className={`table-for-files-button-action margin-none ${isMaster ? 'padding-10' : ''}`}>
          {!isMaster && (
            <>
              <CustomButton
                text={t?.addNewFolder || 'Добавить новую папку'}
                onClick={() => {
                  setIsOpen(true);
                  setIsFolderModalOpen(true);
                }}
              />
              <CustomButtonUpload
                text={t?.addNewFile || 'Добавить новый файл'}
                colorText="#FFFFF"
                fileName="file"
                uploadFileFunc={newFileFromServer}
                name="name"
                currentFolder={currentFolder}
              />
            </>
          )}
        </div>
      </div>

      {/* Модальные окна */}
      {isOpen && (
        <Modal
          isOpen={isOpen}
          onClose={closeModal}
          width={isMoveModalOpen ? '60%' : '700px'}
          height={isMoveModalOpen ? '85%' : '450px'}
        >
          {isFolderModalOpen && (
            <AddFolderModal
              t={t}
              currentFolder={currentFolder}
              onClose={() => {
                closeModal();
                setIsFolderModalOpen(false);
              }}
            />
          )}
          {isMoveModalOpen && (
            <MoveFileModal
              t={t}
              data={files}
              onClose={() => {
                closeModal();
                setIsMoveModalOpen(false);
              }}
              fileToMove={moveFile}
            />
          )}
        </Modal>
      )}
    </>
  );
}

export default TableForFilesManager;
